import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ScanText, ClipboardList, FileSignature, BookOpen, MessageCircleQuestion, Users, ArrowRight, Trophy, Flame, Target } from "lucide-react";
import StreakBadgeWidget from "../components/StreakBadgeWidget";

const tiles = [
  { to: "/app/breakdown", icon: ScanText, title: "Prompt Breakdown", desc: "Decode any writing question.", color: "bg-indigo-100 text-indigo-700" },
  { to: "/app/constructed", icon: ClipboardList, title: "Constructed Response", desc: "Master RACE step-by-step.", color: "bg-rose-100 text-rose-700" },
  { to: "/app/essay", icon: FileSignature, title: "Essay Architect", desc: "Plan a strong outline.", color: "bg-amber-100 text-amber-700" },
  { to: "/app/rubric", icon: BookOpen, title: "Rubric Feedback", desc: "Score your draft fairly.", color: "bg-emerald-100 text-emerald-700" },
  { to: "/app/coach", icon: MessageCircleQuestion, title: "Guided Coach", desc: "Chat with the Socratic coach.", color: "bg-violet-100 text-violet-700" },
  { to: "/app/examples", icon: BookOpen, title: "Examples Library", desc: "Read model writing.", color: "bg-sky-100 text-sky-700" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    api.get("/progress").then((r)=>setProgress(r.data)).catch(()=>{});
  }, []);

  const total = progress?.total_sessions ?? 0;
  const avg = progress?.rubric_trend?.length
    ? (progress.rubric_trend.reduce((a,b)=>a+b.avg,0) / progress.rubric_trend.length).toFixed(1)
    : "—";

  return (
    <div className="space-y-8">
      <div className="fade-up">
        <h1 data-testid="dashboard-title" className="text-3xl sm:text-4xl font-bold" style={{fontFamily:'Outfit'}}>
          Hi {user?.name?.split(' ')[0]} 👋
        </h1>
        <p className="text-slate-600 mt-2">Pick a coach to get started, or peek at your progress below.</p>
      </div>

      {/* Streak + Badges */}
      <div className="fade-up delay-1">
        <StreakBadgeWidget />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 fade-up delay-1">
        <StatCard icon={Flame} label="Sessions" value={total} color="bg-rose-100 text-rose-700" testid="stat-sessions" />
        <StatCard icon={Trophy} label="Avg rubric" value={avg} color="bg-amber-100 text-amber-700" testid="stat-rubric" />
        <StatCard icon={Target} label="Breakdowns" value={progress?.by_kind?.breakdown ?? 0} color="bg-indigo-100 text-indigo-700" testid="stat-breakdowns" />
        <StatCard icon={BookOpen} label="Essays" value={progress?.by_kind?.essay_structure ?? 0} color="bg-emerald-100 text-emerald-700" testid="stat-essays" />
      </div>

      {/* Tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 fade-up delay-2">
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <Link key={t.to} to={t.to} data-testid={`tile-${t.title.toLowerCase().replace(/\s+/g,'-')}`} className="tactile-card p-6 group">
              <div className={`w-12 h-12 rounded-2xl ${t.color} grid place-items-center mb-4`}><Icon size={22}/></div>
              <h3 className="text-lg font-bold mb-1" style={{fontFamily:'Outfit'}}>{t.title}</h3>
              <p className="text-slate-600 text-sm">{t.desc}</p>
              <div className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-indigo-700 group-hover:gap-2 transition-all">
                Open <ArrowRight size={14}/>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Recent activity */}
      <div className="tactile-card p-6 sm:p-8 fade-up delay-3">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold" style={{fontFamily:'Outfit'}}>Recent activity</h2>
          {user?.role === "parent" && (
            <Link to="/app/parent" data-testid="link-parent" className="text-sm font-bold text-indigo-700 inline-flex items-center gap-1">
              Parent Hub <Users size={14} />
            </Link>
          )}
        </div>
        {progress?.recent?.length ? (
          <ul className="divide-y divide-slate-100">
            {progress.recent.map((r) => (
              <li key={r.id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="font-semibold capitalize">{r.kind.replaceAll('_',' ')}</div>
                  <div className="text-sm text-slate-500">{r.created_at?.slice(0,10)}</div>
                </div>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-slate-700">{r.kind.split('_')[0]}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p data-testid="no-activity" className="text-slate-500">No activity yet—try a coach above to get started!</p>
        )}
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, color, testid }) => (
  <div data-testid={testid} className="tactile-card p-5">
    <div className={`w-10 h-10 rounded-xl ${color} grid place-items-center mb-3`}><Icon size={18}/></div>
    <div className="text-3xl font-bold" style={{fontFamily:'Outfit'}}>{value}</div>
    <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mt-1">{label}</div>
  </div>
);
